import Vue from 'vue';

export interface GoogleMapsApi {
  core: google.maps.CoreLibrary
  maps: google.maps.MapsLibrary
  places: google.maps.PlacesLibrary
  geocoding: google.maps.GeocodingLibrary
  routes: google.maps.RoutesLibrary
  marker: google.maps.MarkerLibrary
  geometry: google.maps.GeometryLibrary
  elevation: google.maps.ElevationLibrary
  streetView: google.maps.StreetViewLibrary
  journeySharing: google.maps.JourneySharingLibrary
  drawing: google.maps.DrawingLibrary
  visualization: google.maps.VisualizationLibrary
}
export type GoogleMapsLibraryNames = keyof GoogleMapsApi;

export const GOOGLE_MAPS_SCRIPT_ID = 'glittrGoogleMapsScript';

export default class GoogleMapsProvider {
  isInitialized = false;

  protected initPromise?: Promise<GoogleMapsApi>;

  protected api?: GoogleMapsApi;

  /**
   * Loads Google Maps core and all given libraries.
   * Takes care of checking that Google Maps is loaded exactly once.
   * If already loaded, say by an external script tag, it will use this instance.
   * @returns Initialized Google Maps Api
   */
  async initialize(...libraries: GoogleMapsLibraryNames[]): Promise<GoogleMapsApi> {
    if (this.initPromise) {
      return this.initPromise;
    }
    this.initPromise = new Promise<GoogleMapsApi>((resolve) => {
      // Use an interval instead of google callback,
      // since the callback is global and would not work if initialized multiple times
      const hndl = setInterval(async () => {
        if (typeof window.google?.maps?.importLibrary === 'function') {
          const promises = [] as Promise<any>[];
          libraries.forEach((libName) => {
            promises.push(window.google.maps.importLibrary(libName));
          });
          const loadedLibs = await Promise.all(promises);
          const mapsApi = {} as GoogleMapsApi;
          libraries.forEach((libName, index) => {
            mapsApi[libName] = loadedLibs[index];
          });
          clearInterval(hndl);
          this.isInitialized = true;
          this.api = mapsApi;
          resolve(mapsApi);
        }
      }, 50);

      // init script -> calls callback glitFunc
      if (window.google && window.google.maps && window.google.maps.version) {
        if (!document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) {
          Vue.$log.warning('Google Maps already set and loaded from outside the app');
        }
        return;
      }
      if (document.getElementById(GOOGLE_MAPS_SCRIPT_ID)) {
        return;
      }
      const script = document.createElement('script');
      script.id = GOOGLE_MAPS_SCRIPT_ID;
      const mapsApiKey = Vue.$config.values['google-mapsapikey'];
      const mapsVersion = Vue.$config.values['google-mapsversion'] ?? 'weekly';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${mapsApiKey}&v=${mapsVersion}&loading=async`;
      document.head.appendChild(script);
    });
    (this.initPromise as any).AAA = `${(this.initPromise as any).AAA ?? ''}YES!`;
    return this.initPromise;
  }
}
